import authToken from "../../utils/authToken";
import websocket from "../../utils/getWebsocket";
import generateId from "@/utils/generateId";
import api from "@/api/axios";
import { ucFirst } from "@/mixins/ucFirst";

const connectionId = generateId();

const messageSettings = {
	currentReportMonth: {
		set: 'setReportMonthCurrent'
	},
	prevReportMonth: {
		set: 'setReportMonthPrev'
	},
	reportMonth: {
		set: 'saveReportMonth',
		isArray: true
	},
	report: {
		set: 'saveReport',
		remove: 'deleteReport',
		id: 'idReport'
	},
	schedules: {
		set: 'saveSchedule',
		remove: 'deleteSchedule',
		id: 'idSchedule'
	},
	delivery: {
		set: 'saveDelivery',
		remove: 'deleteDelivery',
		id: 'idDelivery'
	},
	timetable: {
		set: 'setTimetable',
		remove: 'deleteTimetable',
		id: 'idTimetable'
	},
	sales: {
		set: 'saveSales',
		remove: 'deleteSale',
		id: 'idSale'
	},
	services: {
		set: 'saveServices',
		remove: 'deleteService',
		id: 'idService'
	},
	preferences: {
		set: 'savePreferences',
		remove: 'deletePreference',
		id: 'idPreference'
	},
	categories: {
		set: 'setCategory'
	},
	crypto: {
		set: 'setCrypto'
	},
	trades: {
		id: 'idTrades',
		set: 'setSome'
	},
	auth: {
		set: 'setAuth'
	}
}

let wsTimer = null;        // Пинг-таймер (Heartbeat)
let reconnectTimer = null; // Таймер для автореконнекта
let savedAuthData = null;  // Локальное сохранение параметров для реконнекта
let pongTimeoutTimer = null; // Новый таймер для ожидания ответа от PHP

export default {
	state: {
		connection: null,
		data: {},
		activeSubscriptions: []
	},
	getters: {
		WSConnection: state => state.connection,
		WSData: state => state.data
	},
	actions: {
		WSConnect({ commit, state }) {
			if (!state.connection && authToken) {
				if (reconnectTimer) {
					clearTimeout(reconnectTimer);
					reconnectTimer = null;
				}
				
				if (state.connection) {
					state.connection.onopen = null;
					state.connection.onmessage = null;
					state.connection.onclose = null;
					state.connection.close();
				}
				
				const socket = new WebSocket(websocket);
				commit("SET_CONNECTION", socket);
			}
		},
		
		WSConnectionOpen({ state, commit, dispatch }, data = {}) {
			if (!state.connection) return;
			
			const socket = state.connection;
			
			socket.onopen = (event) => {
				state.connection.send(JSON.stringify({
					action: 'getWebSocket',
					connectionId,
					id: authToken,
					...data
				}));
				
				state.activeSubscriptions.forEach(subData => {
					socket.send(JSON.stringify({ action: 'addSubscription', data: subData }));
				});
				
				// Оригинальный Heartbeat-таймер
				if (wsTimer != null) clearInterval(wsTimer);
				
				wsTimer = setInterval(() => {
					if (socket && socket.readyState === WebSocket.OPEN) {
						// 1. Отправляем пинг на PHP бэкенд
						socket.send(JSON.stringify({ action: 'check', connectionId }));
						
						if (pongTimeoutTimer) clearTimeout(pongTimeoutTimer);
						
						pongTimeoutTimer = setTimeout(() => {
							socket.close(); // Это автоматически вызовет onclose и запустит реконнект
						}, 5000);
					}
				}, 25000);
				
				if (data.func) {
					data.func();
				}
			};
			socket.onmessage = async (e) => {
				try {
					let incomingData = JSON.parse(e.data);
					
					if (Array.isArray(incomingData) && (incomingData[0] === '1' || incomingData[0] === '2')) {
						if (pongTimeoutTimer) {
							clearTimeout(pongTimeoutTimer);
							pongTimeoutTimer = null;
						}
						
						if (incomingData[0] === '2') {}
						
						return;
					}
					
					if (!incomingData.data || !incomingData.type) return;
					
					// Очищаем стейт перед записью, как в вашем оригинале
					commit("CLEAR_RAW_DATA");
					
					const setting = messageSettings[incomingData.type];
					
					if (setting) {
						if (incomingData.data.action === 'remove') {
							if (setting.remove) {
								// В экшенах используем commit() из контекста
								commit(setting.remove, { [setting.id]: incomingData.data[setting.id] }, { root: true });
							}
						} else {
							if (setting.set) {
								if (setting.set == 'setSome') {
									commit(setting.set, {
										id: setting.id,
										type: ucFirst(incomingData.type),
										data: incomingData.data
									}, { root: true });
								} else if (setting.set == 'setData') {
									commit(setting.set, {
										type: ucFirst(incomingData.type),
										data: incomingData.data
									}, { root: true });
								} else {
									commit(setting.set, setting.isArray ? [ incomingData.data ] : incomingData.data, { root: true });
								}
							}
						}
					} else {
						commit("SET_RAW_DATA", incomingData);
					}
				} catch (error) {
					console.error("Ошибка парсинга сокет-сообщения:", error);
				}
			};

			socket.onclose = (event) => {
				if (wsTimer != null) {
					clearInterval(wsTimer);
					wsTimer = null;
				}
				
				// Если сокет в стейте совпадает с текущим упавшим сокетом — это случайный обрыв
				if (state.connection === socket) {
					commit("REMOVE_CONNECTION");
					
					reconnectTimer = setTimeout(() => {
						// Последовательно вызываем ваши экшены для переподключения
						dispatch('WSConnect');
						dispatch('WSConnectionOpen', savedAuthData);
					}, 5000);
				}
			};
		},
		
		// Экшен для сохранения подписок из компонентов
		WSSubscribe({ commit, state }, subData) {
			commit("SAVE_SUBSCRIPTION", subData);
		},
		
		WSConnectionClose({ commit, state }) {
			if (reconnectTimer) {
				clearTimeout(reconnectTimer);
				reconnectTimer = null;
			}
			
			if (wsTimer != null) {
				clearInterval(wsTimer);
				wsTimer = null;
			}
			
			if (state.connection) {
				state.connection.onopen = null;
				state.connection.onmessage = null;
				state.connection.onclose = null;
				state.connection.close();
			}
			
			savedAuthData = null;
			commit("REMOVE_CONNECTION");
			commit("CLEAR_SUBSCRIPTIONS");
		},
	},
	mutations: {
		SET_CONNECTION(state, socket) { state.connection = socket; },
		REMOVE_CONNECTION(state) { state.connection = null; },
		CLEAR_RAW_DATA(state) { state.data = {}; },
		SET_RAW_DATA(state, incomingData) { state.data = incomingData; },
		SAVE_SUBSCRIPTION(state, newSub) {
			state.activeSubscriptions = state.activeSubscriptions.filter(existingSub => {
				return existingSub.type !== newSub.type;
			});
			
			if (!newSub.unsubscribe) {
				state.activeSubscriptions.push(newSub);
			}
		},
		CLEAR_SUBSCRIPTIONS(state) { state.activeSubscriptions = []; }
	}
};
